import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const MAX_HISTORY_ENTRIES = 10;

const navigationHistory: string[] = [];

const buildPathKey = (
  pathname: string,
  search: string,
  hash: string
): string => `${pathname}${search ?? ""}${hash ?? ""}`;

/**
 * Mount once at the top of the app (layout) to record route changes into the
 * shared in-memory navigation history.
 */
export function useTrackNavigationHistory(): void {
  const location = useLocation();

  useEffect(() => {
    const fullPath = buildPathKey(
      location.pathname,
      location.search,
      location.hash
    );
    const last = navigationHistory[navigationHistory.length - 1];
    if (last === fullPath) {
      return;
    }
    navigationHistory.push(fullPath);
    if (navigationHistory.length > MAX_HISTORY_ENTRIES) {
      navigationHistory.shift();
    }
  }, [location.pathname, location.search, location.hash]);
}

/**
 * Returns the navigation entry that preceded the current one, or `null` if the
 * user landed directly on the current route (no in-app history yet).
 */
export function getPreviousNavigationEntry(): string | null {
  if (navigationHistory.length < 2) {
    return null;
  }
  return navigationHistory[navigationHistory.length - 2] ?? null;
}

/** Test/escape hatch: clears the in-memory history. */
export function _resetNavigationHistory(): void {
  navigationHistory.length = 0;
}
